import axios from "axios";
import { parseCookies } from "nookies";
import {
  TRAINING_CATEGORIES_LIST_URL,
  TRAINING_CATEGORIES_CREATE_URL,
  TRAINING_CATEGORIES_DETAIL_URL,
  TRAINING_CATEGORIES_UPDATE_URL,
  TRAINING_CATEGORIES_DELETE_URL,
  TRAINING_CATEGORIES_VIDEOS_URL,
  TRAINING_CATEGORIES_PROGRESS_URL,
  TRAINING_CATEGORIES_USER_OVERVIEW_URL,
  TRAINING_VIDEOS_LIST_URL,
  TRAINING_VIDEOS_CREATE_URL,
  TRAINING_VIDEOS_DETAIL_URL,
  TRAINING_VIDEOS_UPDATE_URL,
  TRAINING_VIDEOS_DELETE_URL,
  TRAINING_VIDEOS_ACCESS_CHECK_URL,
  TRAINING_VIDEOS_PLAYER_DATA_URL,
  TRAINING_VIDEOS_URL, // Adicionar para upload local
  TRAINING_PROGRESS_LIST_URL,
  TRAINING_PROGRESS_START_VIDEO_URL,
  TRAINING_PROGRESS_UPDATE_PROGRESS_URL,
  TRAINING_PROGRESS_COMPLETE_VIDEO_URL,
  TRAINING_PROGRESS_MY_SUMMARY_URL,
  TRAINING_PROGRESS_DASHBOARD_URL,
  TRAINING_REPORTS_USERS_PROGRESS_URL,
  TRAINING_REPORTS_VIDEOS_ANALYTICS_URL,
  TRAINING_REPORTS_CATEGORY_OVERVIEW_URL,
} from "../api/Urls";

const getAuthHeaders = (isFormData = false) => {
  const { token } = parseCookies();
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  
  // Para FormData, deixar o browser definir Content-Type automaticamente
  if (!isFormData) {
    headers["Content-Type"] = "application/json";
  }
  
  return headers;
};

// =============================================== //
// CATEGORIES SERVICES                            //
// =============================================== //

export const getTrainingCategories = async () => {
  try {
    const response = await axios.get(TRAINING_CATEGORIES_LIST_URL, {
      headers: getAuthHeaders(),
    });
    // Se a API retorna dados paginados, extrair o array results
    return response.data.results || response.data;
  } catch (error) {
    console.error("Error fetching training categories:", error);
    throw error;
  }
};

export const createTrainingCategory = async (categoryData) => {
  try {
    const response = await axios.post(TRAINING_CATEGORIES_CREATE_URL, categoryData, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    console.error("Error creating training category:", error);
    throw error;
  }
};

export const getTrainingCategory = async (categoryId) => {
  try {
    const response = await axios.get(TRAINING_CATEGORIES_DETAIL_URL(categoryId), {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching training category:", error);
    throw error;
  }
};

export const updateTrainingCategory = async (categoryId, categoryData) => {
  try {
    const response = await axios.put(TRAINING_CATEGORIES_UPDATE_URL(categoryId), categoryData, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    console.error("Error updating training category:", error);
    throw error;
  }
};

export const deleteTrainingCategory = async (categoryId) => {
  try {
    const response = await axios.delete(TRAINING_CATEGORIES_DELETE_URL(categoryId), {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    console.error("Error deleting training category:", error);
    throw error;
  }
};

export const getCategoryVideos = async (categoryId) => {
  try {
    const response = await axios.get(TRAINING_CATEGORIES_VIDEOS_URL(categoryId), {
      headers: getAuthHeaders(),
    });
    // Se a API retorna dados paginados, extrair o array results
    return response.data.results || response.data;
  } catch (error) {
    console.error("Error fetching category videos:", error);
    throw error;
  }
};

export const getCategoryProgress = async (categoryId) => {
  try {
    const response = await axios.get(TRAINING_CATEGORIES_PROGRESS_URL(categoryId), {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching category progress:", error);
    throw error;
  }
};

export const getUserOverview = async () => {
  try {
    const response = await axios.get(TRAINING_CATEGORIES_USER_OVERVIEW_URL, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching user overview:", error);
    throw error;
  }
};

// =============================================== //
// VIDEOS SERVICES                               //
// =============================================== //

export const getTrainingVideos = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    if (params.category_id) queryParams.append('category_id', params.category_id);
    if (params.unlocked_only) queryParams.append('unlocked_only', params.unlocked_only);
    
    const url = queryParams.toString() 
      ? `${TRAINING_VIDEOS_LIST_URL}?${queryParams.toString()}`
      : TRAINING_VIDEOS_LIST_URL;
      
    const response = await axios.get(url, {
      headers: getAuthHeaders(),
    });
    // Se a API retorna dados paginados, extrair o array results
    return response.data.results || response.data;
  } catch (error) {
    console.error("Error fetching training videos:", error);
    throw error;
  }
};

export const createTrainingVideo = async (videoData) => {
  try {
    const isFormData = videoData instanceof FormData;
    const response = await axios.post(TRAINING_VIDEOS_CREATE_URL, videoData, {
      headers: getAuthHeaders(isFormData),
    });
    return response.data;
  } catch (error) {
    console.error("Error creating training video:", error);
    throw error;
  }
};

export const getTrainingVideo = async (videoId) => {
  try {
    const response = await axios.get(TRAINING_VIDEOS_DETAIL_URL(videoId), {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching training video:", error);
    throw error;
  }
};

export const updateTrainingVideo = async (videoId, videoData) => {
  try {
    const isFormData = videoData instanceof FormData;
    const response = await axios.put(TRAINING_VIDEOS_UPDATE_URL(videoId), videoData, {
      headers: getAuthHeaders(isFormData),
    });
    return response.data;
  } catch (error) {
    console.error("Error updating training video:", error);
    throw error;
  }
};

export const deleteTrainingVideo = async (videoId) => {
  try {
    const response = await axios.delete(TRAINING_VIDEOS_DELETE_URL(videoId), {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    console.error("Error deleting training video:", error);
    throw error;
  }
};

// 📤 NOVO: Upload Local de Vídeos para usuários staff
export const uploadLocalVideo = async (videoData, onUploadProgress = null) => {
  try {
    // Garantir que os dados sejam FormData
    if (!(videoData instanceof FormData)) {
      throw new Error("uploadLocalVideo requer FormData com video_file");
    }

    console.group("🎬 UPLOAD LOCAL VIDEO - DEBUG");
    console.log("📤 Enviando dados para endpoint /upload-local/");
    
    // Log dos dados do FormData (debug)
    for (let [key, value] of videoData.entries()) {
      if (value instanceof File) {
        console.log(`📁 ${key}:`, {
          name: value.name,
          size: value.size,
          type: value.type
        });
      } else {
        console.log(`📝 ${key}:`, value);
      }
    }

    const config = {
      headers: {
        ...getAuthHeaders(true), // FormData headers
        // Não definir Content-Type manualmente para FormData
      },
      timeout: 300000, // 5 minutos timeout para uploads grandes
    };

    // Adicionar callback de progresso se fornecido
    if (onUploadProgress && typeof onUploadProgress === 'function') {
      config.onUploadProgress = (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onUploadProgress({
          loaded: progressEvent.loaded,
          total: progressEvent.total,
          progress: percentCompleted
        });
        console.log(`📊 Upload progress: ${percentCompleted}%`);
      };
    }

    const uploadUrl = `${TRAINING_VIDEOS_URL}upload-local/`;
    console.log("🔗 Upload URL:", uploadUrl);

    const response = await axios.post(uploadUrl, videoData, config);
    
    console.log("✅ Upload local successful:", response.data);
    console.groupEnd();
    
    return response.data;
  } catch (error) {
    console.group("❌ UPLOAD LOCAL ERROR");
    console.error("📤 Error uploading local video:", error);
    
    if (error.response) {
      console.error("📊 Response status:", error.response.status);
      console.error("📋 Response data:", error.response.data);
      console.error("📄 Response headers:", error.response.headers);
      
      // Erros específicos baseados na documentação
      if (error.response.status === 403) {
        console.error("🚫 Upload local requer is_staff = true");
      } else if (error.response.status === 400) {
        console.error("⚠️ Erro de validação - verificar campos obrigatórios e tamanho do arquivo");
      }
    }
    
    console.groupEnd();
    throw error;
  }
};

export const checkVideoAccess = async (videoId) => {
  try {
    const response = await axios.get(TRAINING_VIDEOS_ACCESS_CHECK_URL(videoId), {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    console.error("Error checking video access:", error);
    throw error;
  }
};

export const getVideoPlayerData = async (videoId) => {
  try {
    const response = await axios.get(TRAINING_VIDEOS_PLAYER_DATA_URL(videoId), {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// =============================================== //
// VIDEO LINKS (MATERIAIS AUXILIARES)             //
// =============================================== //

export const getVideoLinks = async (videoId) => {
  try {
    const response = await axios.get(`${TRAINING_VIDEOS_URL}${videoId}/links/`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createVideoLink = async (videoId, linkData) => {
  try {
    const response = await axios.post(`${TRAINING_VIDEOS_URL}${videoId}/links/`, linkData, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateVideoLink = async (videoId, linkId, linkData) => {
  try {
    const response = await axios.put(`${TRAINING_VIDEOS_URL}${videoId}/links/${linkId}/`, linkData, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteVideoLink = async (videoId, linkId) => {
  try {
    const response = await axios.delete(`${TRAINING_VIDEOS_URL}${videoId}/links/${linkId}/`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// =============================================== //
// PROGRESS SERVICES                             //
// =============================================== //

export const getTrainingProgress = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    if (params.video_id) queryParams.append('video_id', params.video_id);
    if (params.category_id) queryParams.append('category_id', params.category_id);
    if (params.completed_only) queryParams.append('completed_only', params.completed_only);
    
    const url = queryParams.toString() 
      ? `${TRAINING_PROGRESS_LIST_URL}?${queryParams.toString()}`
      : TRAINING_PROGRESS_LIST_URL;
      
    const response = await axios.get(url, {
      headers: getAuthHeaders(),
    });
    // Se a API retorna dados paginados, extrair o array results
    return response.data.results || response.data;
  } catch (error) {
    console.error("Error fetching training progress:", error);
    throw error;
  }
};

export const startVideo = async (videoId) => {
  try {
    const response = await axios.post(TRAINING_PROGRESS_START_VIDEO_URL, 
      { video_id: videoId },
      {
        headers: getAuthHeaders(),
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error starting video:", error);
    throw error;
  }
};

export const updateProgress = async (videoId, progressPercent, lastPositionSeconds, totalWatchTime) => {
  try {
    console.log("TrainingService - Sending progress update:", {
      video_id: videoId,
      progress_percent: progressPercent,
      last_position_seconds: lastPositionSeconds,
      total_watch_time: totalWatchTime,
    });
    
    const response = await axios.post(TRAINING_PROGRESS_UPDATE_PROGRESS_URL, 
      {
        video_id: videoId,
        progress_percent: progressPercent,
        last_position_seconds: lastPositionSeconds,
        total_watch_time: totalWatchTime,
      },
      {
        headers: getAuthHeaders(),
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error updating progress:", error);
    console.error("Request payload:", {
      video_id: videoId,
      progress_percent: progressPercent,
      last_position_seconds: lastPositionSeconds,
      total_watch_time: totalWatchTime,
    });
    throw error;
  }
};

export const completeVideo = async (videoId) => {
  try {
    const response = await axios.post(TRAINING_PROGRESS_COMPLETE_VIDEO_URL, 
      { video_id: videoId },
      {
        headers: getAuthHeaders(),
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error completing video:", error);
    throw error;
  }
};

export const getMyProgressSummary = async () => {
  try {
    const response = await axios.get(TRAINING_PROGRESS_MY_SUMMARY_URL, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching progress summary:", error);
    throw error;
  }
};

export const getTrainingDashboard = async () => {
  try {
    const response = await axios.get(TRAINING_PROGRESS_DASHBOARD_URL, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching training dashboard:", error);
    throw error;
  }
};

// =============================================== //
// REPORTS SERVICES (Admin only)                 //
// =============================================== //

export const getUsersProgress = async () => {
  try {
    const response = await axios.get(TRAINING_REPORTS_USERS_PROGRESS_URL, {
      headers: getAuthHeaders(),
    });
    // Se a API retorna dados paginados, extrair o array results
    return response.data.results || response.data;
  } catch (error) {
    console.error("Error fetching users progress:", error);
    throw error;
  }
};

export const getVideosAnalytics = async () => {
  try {
    const response = await axios.get(TRAINING_REPORTS_VIDEOS_ANALYTICS_URL, {
      headers: getAuthHeaders(),
    });
    // Se a API retorna dados paginados, extrair o array results
    return response.data.results || response.data;
  } catch (error) {
    console.error("Error fetching videos analytics:", error);
    throw error;
  }
};

export const getCategoryOverview = async () => {
  try {
    const response = await axios.get(TRAINING_REPORTS_CATEGORY_OVERVIEW_URL, {
      headers: getAuthHeaders(),
    });
    // Se a API retorna dados paginados, extrair o array results
    return response.data.results || response.data;
  } catch (error) {
    console.error("Error fetching category overview:", error);
    throw error;
  }
};